<template>
  <base-wrapper :loading="loading || loadingJobStatus || editArea.loading">
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <base-input :shadow="false" type="text" placeholder="Cari Nama Wilayah" debounce v-model="filter.search" @native-input="loadCities" />
        <base-button :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </base-button>
      </div>
      <datatable
        :columns="columns"
        :total="cities.meta.page.total"
        :perPage="cities.meta.page.perPage"
        :currentPage="cities.meta.page.currentPage"
        @pagechanged="onPagechanged"
      >
        <template v-slot:tbody="{ classes }">
          <tr
            v-for="city in cities.data"
            :key="city.id"
            :class="[classes.tr, 'bg-white']"
          >
            <td :class="[classes.td]">
              <p class="font-bold text-gray-900">
                {{ city.attributes.code || '-' }}
              </p>
            </td>
            <td :class="[classes.td]">
              {{ city.attributes.name }}
            </td>
            <td :class="[classes.td]">
              {{ getSingleIncluded(cities, city.relationships.province.data.id).attributes.name }}
            </td>
            <td :class="[classes.td]">
              {{ city.attributes.area_code }}
            </td>
            <td :class="[classes.td]">
              <base-button size="sm" @click="onOpenEditArea(city)">Edit Area</base-button>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <BaseConfirm :visible="editArea.visible" icon-type="badge" type="success" title="Edit Area"
      @close="editArea.visible = false" @confirmed="onConfirmEditArea">
      <template #content>
        <BaseSelect expand :shadow="null" :options="areaOptions" v-model="editArea.input" />
      </template>
    </BaseConfirm>
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';
import { downloadFileUrl } from '../../../../../services/utils.service';
import BaseConfirm from '../../../../../components/base/BaseConfirm.vue';

export default {
  mixins: [requestMixin],
  components: {
    BaseConfirm
  },
  data() {
    return {
      filter: {
        search: null
      },
      loading: false,
      loadingJobStatus: false,
      cities: {
        data: [],
        meta: {
          page: {},
        },
      },
      loadingExport: {
        visible: false,
        text: null,
      },
      editArea: {
        visible: false,
        input: 0,
        loading: false,
        cityId: null
      },
      areas: {
        data: []
      }
    };
  },
  computed: {
    ...mapGetters({
      getChannelName: 'auth/getChannelName',
    }),
    columns() {
      return [
        {
          id: 'code',
          name: 'Kode Kota',
        },
        {
          id: 'name',
          name: 'Nama Kota',
        },
        {
          id: 'province',
          name: 'Provinsi',
        },
        {
          id: 'area',
          name: 'Area',
        },
        {
          id: 'Aksi',
          name: 'Aksi',
        },
      ];
    },
    areaOptions() {
      return this.areas.data.map(area => ({ key: area.id, label: area.attributes.code, value: area.id }))
    }
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert'
    }),
    onPagechanged(page) {
      this.loadCities({
        'page[number]': page,
      });
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/cities/-actions/export`,
        {
          method: 'post'
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\CityExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport wilayah',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'wilayah.csv', { blank: true });
        }
      }
    },
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    onOpenEditArea(city) {
      this.editArea.cityId = city.id
      this.editArea.input = city.relationships.area.data.id
      this.editArea.visible = true
    },
    async onConfirmEditArea() {
      this.editArea.loading = true;

      const [, error] = await this.request(`/api/v1/cities/${this.editArea.cityId}`, {
        method: 'patch',
        data: {
          data: {
            type: 'cities',
            id: this.editArea.cityId,
            relationships: {
              area: {
                data: {
                  type: 'areas',
                  id: this.editArea.input
                }
              }
            }
          }
        }
      });

      if (error) {
        this.createAlert({ status: 'error', data: this.getRequestErrorMessage(error) })
      } else {
        this.loadCities()
        this.createAlert({ status: 'success', data: 'Area berhasil diperbarui' })

        this.editArea.visible = false
      }
      
      this.editArea.loading = false;
    },
    async loadCities(params) {
      this.loading = true;

      const [res, error] = await this.request('/api/v1/cities', {
        params: {
          'page[size]': 5,
          'sort': '-createdAt',
          'include': 'province,area',
          'filter[search]': this.filter.search,
          ...params,
        },
      });

      if (!error) {
        this.cities = res;
      }

      this.loading = false;
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_wilayah',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
    removeExportListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onExportProgress
        );
      }
    },
    async loadAreas() {
      const [res, error] = await this.request('/api/v1/areas');

      if (!error) {
        this.areas = res;
      }
    },
  },
  created() {
    this.loadCities();
    this.loadJobStatus();
    this.addListener()
    this.loadAreas()
  },
  beforeRouteLeave(to, from, next) {
    this.removeExportListener();

    next();
  },
};
</script>
